import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldCheck, Plus, Check, Loader2, BookOpen, Package, Sparkles, BrainCircuit,
  Lock, TrendingUp, Headset, Users, MessageSquare, BarChart3, Code2, Megaphone,
  HardHat, Wallet, Languages, Scale, HeartHandshake, Leaf, Briefcase, Mail,
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';

const P = (o = 1) => `rgb(var(--brand-primary-rgb, 0 255 255) / ${o})`;
const A = (o = 1) => `rgb(var(--brand-accent-rgb, 204 255 0) / ${o})`;
const S = (o = 1) => `rgb(var(--brand-secondary-rgb, 168 85 247) / ${o})`;

/* ── Icono según la temática del curso ──────────────────────────────────
   Se busca por palabras clave dentro del título (sin tildes, en minúsculas).
   El orden importa: la primera regla que coincide gana.
   Para añadir una temática nueva, basta con añadir una línea aquí.        */
const TOPIC_ICONS = [
  [/scorm|paquete|lms/,                                   Package],
  [/copilot|prompt|indicacion|m365|microsoft|office/,     Sparkles],
  [/\bia\b|inteligencia artificial|machine|chatgpt|gemini/, BrainCircuit],
  [/ciberseg|seguridad inform|phishing|contrasen|rgpd|proteccion de datos|privacidad|lopd/, Lock],
  [/riesgos|prevencion|prl|laboral|seguridad y salud|emergencia/, HardHat],
  [/venta|comercial|negociaci/,                           TrendingUp],
  [/cliente|atencion|soporte|servicio/,                   Headset],
  [/lider|equipo|gestion de personas|rrhh|recursos humanos/, Users],
  [/comunicaci|oratoria|presentaci|escucha/,              MessageSquare],
  [/excel|datos|analitica|analytics|power bi|estadistic/, BarChart3],
  [/program|codigo|python|javascript|desarrollo|software/, Code2],
  [/marketing|redes sociales|publicidad|branding/,        Megaphone],
  [/finanza|contab|presupuesto|econom|fiscal/,            Wallet],
  [/idioma|ingles|frances|aleman|language/,               Languages],
  [/etica|normativa|compliance|legal|cumplimiento|igualdad|acoso/, Scale],
  [/bienestar|salud|mindfulness|estres|emocional|diversidad|inclusi/, HeartHandshake],
  [/sostenib|medioambient|ambiental|ecolog/,              Leaf],
  [/correo|email|outlook/,                                Mail],
  [/gestion|proyecto|productividad|organizaci|empresa/,   Briefcase],
];

const normalize = (s = '') =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const iconForTitle = (title) => {
  const t = normalize(title);
  const hit = TOPIC_ICONS.find(([re]) => re.test(t));
  return hit ? hit[1] : BookOpen;
};

const TABS = [
  { key: 'mandatory', label: 'Obligatorios', empty: 'No tienes módulos obligatorios.' },
  { key: 'general',   label: 'Generales',    empty: 'No hay módulos generales disponibles.' },
];

const ModulesCatalog = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState('mandatory');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/modules/catalog');
      setItems(r.data || []);
    } catch {
      toast.error('Error al cargar los módulos');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (m) => {
    setBusyId(m.id);
    try {
      if (m.in_my_modules) {
        await api.delete(`/modules/${m.id}/add-to-my-modules`);
        toast.success('Quitado de Mis Módulos');
      } else {
        await api.post(`/modules/${m.id}/add-to-my-modules`);
        toast.success('Añadido a Mis Módulos');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setBusyId(null);
    }
  };

  const groups = useMemo(() => ({
    mandatory: items.filter(m => m.mandatory),
    general: items.filter(m => !m.mandatory),
  }), [items]);

  const current = groups[tab];
  const currentTab = TABS.find(t => t.key === tab);

  const cardStyle = {
    background: 'linear-gradient(rgb(0 0 0 / .6), rgb(0 0 0 / .6)), rgb(var(--brand-bg-rgb, 6 5 20) / .9)',
    border: `1px solid ${P(0.35)}`,
    borderRadius: 16,
  };

  const ModuleCard = ({ m }) => {
    const Icon = iconForTitle(m.title);
    const added = m.in_my_modules;

    return (
      <div className="flex items-center gap-4 p-4 min-h-[88px]" style={cardStyle}>
        {/* Icono de la temática */}
        <span
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: P(0.14), border: `1px solid ${P(0.45)}`, color: 'var(--brand-primary, #00FFFF)' }}
        >
          <Icon className="w-6 h-6" />
        </span>

        {/* Título (máx. 2 líneas) */}
        <span
          className="flex-1 min-w-0 text-white font-bold text-[15px] leading-snug"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          title={m.title}
        >
          {m.title}
        </span>

        {/* Acción a la derecha */}
        {m.mandatory ? (
          <span
            title="Obligatorio · ya está en Mis Módulos"
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: A(0.18), border: `1px solid ${A(0.5)}`, color: 'var(--brand-accent, #CCFF00)' }}
          >
            <ShieldCheck className="w-4 h-4" />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => toggle(m)}
            disabled={busyId === m.id}
            title={added ? 'En Mis Módulos (pulsa para quitar)' : 'Añadir a Mis Módulos'}
            aria-label={added ? 'Quitar de Mis Módulos' : 'Añadir a Mis Módulos'}
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform hover:scale-110 disabled:opacity-50"
            style={added
              ? { background: S(0.25), border: `1px solid ${S(0.7)}`, color: '#fff' }
              : { background: P(0.18), border: `1px solid ${P(0.5)}`, color: 'var(--brand-primary, #00FFFF)' }}
          >
            {busyId === m.id
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : added ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 rounded-full animate-spin"
          style={{ borderColor: 'var(--brand-primary, #00FFFF)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 relative">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight"
          style={{ color: 'var(--brand-text, #fff)', fontFamily: 'Roberta, serif' }}>
          Módulos
        </h1>
        <p className="text-white/70 text-sm mt-2 font-medium">
          Los obligatorios ya están en Mis Módulos. Añade los generales que quieras realizar.
        </p>
      </div>

      {/* Pestañas, una al lado de la otra */}
      <div role="tablist" aria-label="Tipo de módulo" className="flex gap-2 mb-6">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors"
              style={active
                ? { background: P(0.2), border: `1px solid ${P(0.8)}`, color: 'var(--brand-primary, #00FFFF)' }
                : { background: 'rgb(0 0 0 / .45)', border: '1px solid rgb(255 255 255 / .15)', color: 'rgb(255 255 255 / .65)' }}
            >
              {t.label}
              <span
                className="min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-black flex items-center justify-center"
                style={active
                  ? { background: 'var(--brand-primary, #00FFFF)', color: '#000' }
                  : { background: 'rgb(255 255 255 / .15)', color: '#fff' }}
              >
                {groups[t.key].length}
              </span>
            </button>
          );
        })}
      </div>

      {/* Rejilla de 2 columnas */}
      {current.length === 0 ? (
        <p className="text-white/40 text-sm">{currentTab.empty}</p>
      ) : (
        <div role="tabpanel" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {current.map(m => <ModuleCard key={m.id} m={m} />)}
        </div>
      )}
    </div>
  );
};

export default ModulesCatalog;